// Низкоуровневый HTTP-движок REST-клиента. SPEC client-transport §4.2.
//
// Базовые правила всех REST-вызовов:
//   - Cookie-сессия (D-07): `credentials: 'include'`; токен в httpOnly-cookie, JS его не трогает,
//     никаких Authorization-заголовков.
//   - CSRF на мутациях (POST/PUT/PATCH/DELETE): `X-Requested-With: tele-chat` (двойная защита к
//     SameSite, SPEC §4.2) + `X-CSRF-Token` double-submit из НЕ-httpOnly cookie (R2-бэклог E).
//   - Единый разбор ErrorEnvelope: не-2xx → читаем тело как конверт → throw TransportError
//     (errors.ts). Не-конверт (502/HTML/обрыв) → INTERNAL.
//   - 401 UNAUTHENTICATED: помимо throw — колбэк `onUnauthenticated` (фабрика эмитит auth.required).
//   - Таймаут/отмена: внешний AbortSignal + внутренний дефолт-таймаут через AbortController.
import { TransportError, envelopeToError, networkToError } from './errors.js';
/**
 * Нижняя граница скорости отправки, под которую считается таймаут выгрузки тела (UP-1): ~32 КБ/с —
 * очень слабый мобильный канал. Общий дефолт 30 с резал куски по 8 МиБ уже на ~2 Мбит/с (прод форка
 * «Наш.Чат» 28.09.2026: pptx 18 МБ с телефона дважды падал ровно на 30-й секунде).
 */
export const UPLOAD_MIN_BYTES_PER_SEC = 32 * 1024;
/** Пол таймаута выгрузки (мс) — мелкие куски не должны получать меньше обычного запроса с запасом. */
export const UPLOAD_MIN_TIMEOUT_MS = 60_000;
/**
 * Нижняя граница скорости ПРИЁМА, под которую считается таймаут скачивания тела (DLP-1): ~16 КиБ/с —
 * почти оборванный мобильный канал; домашний сервер форка отдаёт медиа через обычный аплинк.
 */
export const DOWNLOAD_MIN_BYTES_PER_SEC = 16 * 1024;
/** Пол таймаута скачивания (мс). */
export const DOWNLOAD_MIN_TIMEOUT_MS = 60_000;
/** Таймаут скачивания тела размером `bytes` (заголовки + всё тело): не меньше пола. */
export function downloadTimeoutMs(bytes) {
    return Math.max(DOWNLOAD_MIN_TIMEOUT_MS, Math.ceil((bytes / DOWNLOAD_MIN_BYTES_PER_SEC) * 1000));
}
/** Таймаут выгрузки тела размером `bytes`: время на UPLOAD_MIN_BYTES_PER_SEC, не меньше пола. */
export function uploadTimeoutMs(bytes) {
    return Math.max(UPLOAD_MIN_TIMEOUT_MS, Math.ceil((bytes / UPLOAD_MIN_BYTES_PER_SEC) * 1000));
}
/** Имя не-httpOnly cookie с CSRF-токеном (double-submit). Согласовано с api-server (R2 E). */
export const CSRF_COOKIE = 'csrf_token';
/** Заголовок CSRF double-submit (R2 E). */
export const CSRF_HEADER = 'X-CSRF-Token';
/** Заголовок-маркер происхождения запроса (SPEC §4.2, двойная защита к SameSite). */
export const REQUESTED_WITH_HEADER = 'X-Requested-With';
export const REQUESTED_WITH_VALUE = 'tele-chat';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** Чтение значения cookie по имени из строки `document.cookie`-совместимого вида. */
export function readCookie(cookieString, name) {
    if (cookieString.length === 0)
        return undefined;
    for (const part of cookieString.split(';')) {
        const idx = part.indexOf('=');
        if (idx < 0)
            continue;
        const k = part.slice(0, idx).trim();
        if (k === name)
            return decodeURIComponent(part.slice(idx + 1).trim());
    }
    return undefined;
}
/** Дефолтный источник CSRF-токена: читает не-httpOnly cookie из `document.cookie`, если он есть. */
export function defaultCsrfSource() {
    return () => {
        if (typeof document === 'undefined')
            return undefined;
        return readCookie(document.cookie, CSRF_COOKIE);
    };
}
/**
 * HTTP-движок: типобезопасный `request` с разбором ответа по дженерику `T`. Возвращает `T`
 * (распарсенный JSON) — формы тел/ответов задаёт REST-клиент по таблице shared-contracts §4.5.
 */
export class HttpEngine {
    baseUrl;
    fetchImpl;
    csrfSource;
    onUnauthenticated;
    defaultTimeoutMs;
    xhrFactory;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
        this.fetchImpl = opts.fetchImpl;
        this.csrfSource = opts.csrfSource ?? defaultCsrfSource();
        this.onUnauthenticated = opts.onUnauthenticated;
        this.defaultTimeoutMs = opts.defaultTimeoutMs ?? 30_000;
        this.xhrFactory = opts.xhrFactory;
    }
    /** GET → JSON `T`. */
    get(path, query, opts) {
        return this.request('GET', this.withQuery(path, query), { kind: 'none' }, opts);
    }
    /** POST JSON-тело → JSON `T`. */
    postJson(path, body, opts) {
        return this.request('POST', path, { kind: 'json', value: body }, opts);
    }
    /** POST без тела → JSON `T`. */
    post(path, opts) {
        return this.request('POST', path, { kind: 'none' }, opts);
    }
    /** PATCH JSON-тело → JSON `T`. */
    patchJson(path, body, opts) {
        return this.request('PATCH', path, { kind: 'json', value: body }, opts);
    }
    /** DELETE с опциональным JSON-телом → JSON `T`. */
    deleteJson(path, body, opts) {
        return this.request('DELETE', path, body === undefined ? { kind: 'none' } : { kind: 'json', value: body }, opts);
    }
    /** POST multipart/form-data (аватар) → JSON `T`. SPEC §4.2/§4.5. */
    postForm(path, form, opts) {
        return this.request('POST', path, { kind: 'form', value: form }, opts);
    }
    /**
     * GET → Blob (байты вложения под cookie-сессией; iOS-standalone share-путь скачивания).
     * Конверт ошибок/401-колбэк — те же, что у JSON-путей; тело успеха НЕ парсится как JSON.
     */
    async getBlob(path, opts) {
        // DLP-1: таймаут и внешняя отмена действуют до КОНЦА тела (раньше cleanup сразу после заголовков
        // — тело большого файла качалось без дедлайна, а кнопка «Отмена» ничего не могла прервать).
        const { signal, cleanup } = this.makeSignal(opts);
        try {
            let res;
            try {
                res = await this.fetchImpl(this.baseUrl + path, {
                    method: 'GET',
                    credentials: 'include', // cookie-сессия (D-07)
                    signal,
                });
            }
            catch (cause) {
                if (isAbort(cause))
                    throw new TransportError('INTERNAL', 'request aborted', { cause });
                throw networkToError(cause);
            }
            if (res.ok) {
                try {
                    return await readBlob(res, signal, opts?.onDownloadProgress);
                }
                catch (cause) {
                    if (cause instanceof TransportError)
                        throw cause;
                    if (isAbort(cause) || signal.aborted)
                        throw new TransportError('INTERNAL', 'request aborted', { cause });
                    throw networkToError(cause);
                }
            }
            const errBody = await this.readBodySafe(res);
            const error = envelopeToError(res.status, errBody);
            if (error.code === 'UNAUTHENTICATED' || res.status === 401) {
                this.onUnauthenticated?.();
            }
            throw error;
        }
        finally {
            cleanup();
        }
    }
    /**
     * PUT сырого тела (chunk-загрузка с Content-Range) → JSON `T`. SPEC §4.5. С `onUploadProgress` и
     * доступным XHR — через XHR (прогресс отправки, UP-1); иначе через fetch, как прежде.
     */
    putRaw(path, body, extraHeaders, contentType, opts) {
        const raw = { kind: 'raw', value: body, contentType, extraHeaders };
        if (opts?.onUploadProgress !== undefined && this.xhrFactory !== undefined && body instanceof Blob) {
            return this.requestXhr('PUT', path, raw, body, opts, opts.onUploadProgress, this.xhrFactory);
        }
        return this.request('PUT', path, raw, opts);
    }
    /** Заголовки и тело запроса: Accept, CSRF на мутациях, Content-Type по виду тела. */
    buildRequest(method, body) {
        const headers = new Headers();
        headers.set('Accept', 'application/json');
        // CSRF на мутациях: маркер происхождения + double-submit токен (если cookie есть).
        if (MUTATING.has(method)) {
            headers.set(REQUESTED_WITH_HEADER, REQUESTED_WITH_VALUE);
            const csrf = this.csrfSource();
            if (csrf !== undefined && csrf.length > 0)
                headers.set(CSRF_HEADER, csrf);
        }
        let payload;
        switch (body.kind) {
            case 'json':
                headers.set('Content-Type', 'application/json');
                payload = JSON.stringify(body.value);
                break;
            case 'blob':
                if (body.contentType !== undefined)
                    headers.set('Content-Type', body.contentType);
                payload = body.value;
                break;
            case 'form':
                // Content-Type для multipart выставляет fetch сам (с boundary) — НЕ задаём вручную.
                payload = body.value;
                break;
            case 'raw':
                if (body.contentType !== undefined)
                    headers.set('Content-Type', body.contentType);
                if (body.extraHeaders)
                    for (const [k, v] of Object.entries(body.extraHeaders))
                        headers.set(k, v);
                payload = body.value;
                break;
            case 'none':
                payload = undefined;
                break;
        }
        return { headers, payload };
    }
    /** Базовый запрос: собирает заголовки/тело, шлёт, разбирает ответ/ошибку. */
    async request(method, path, body, opts) {
        const { headers, payload } = this.buildRequest(method, body);
        const { signal, cleanup } = this.makeSignal(opts);
        let res;
        try {
            res = await this.fetchImpl(this.baseUrl + path, {
                method,
                headers,
                body: payload,
                credentials: 'include', // cookie-сессия (D-07)
                signal,
            });
        }
        catch (cause) {
            cleanup();
            // Отмена по таймауту/внешнему сигналу или сетевой сбой — всё → INTERNAL (нет HTTP-статуса).
            if (isAbort(cause))
                throw new TransportError('INTERNAL', 'request aborted', { cause });
            throw networkToError(cause);
        }
        cleanup();
        if (res.ok) {
            return (await this.parseJson(res));
        }
        // Не-2xx: единый разбор ErrorEnvelope.
        const errBody = await this.readBodySafe(res);
        const error = envelopeToError(res.status, errBody);
        if (error.code === 'UNAUTHENTICATED' || res.status === 401) {
            this.onUnauthenticated?.();
        }
        throw error;
    }
    /**
     * Тот же запрос через XHR — ради прогресса отправки (UP-1). Семантика как у fetch-пути: те же
     * заголовки/CSRF, cookie-сессия (withCredentials), таймаут+внешняя отмена, конверт ошибок, 401-колбэк.
     */
    requestXhr(method, path, body, blob, opts, onProgress, xhrFactory) {
        const { headers } = this.buildRequest(method, body);
        const { signal, cleanup } = this.makeSignal(opts);
        return new Promise((resolve, reject) => {
            const xhr = xhrFactory();
            let settled = false;
            const finish = (fn) => {
                if (settled)
                    return;
                settled = true;
                cleanup();
                signal.removeEventListener('abort', onAbort);
                fn();
            };
            const onAbort = () => {
                // Сначала фиксируем исход с причиной (таймаут/внешняя отмена), потом рвём XHR: его onabort
                // сработает синхронно и должен застать запрос уже завершённым.
                finish(() => reject(new TransportError('INTERNAL', 'request aborted', { cause: signal.reason })));
                xhr.abort();
            };
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener('abort', onAbort, { once: true });
            xhr.open(method, this.baseUrl + path);
            xhr.withCredentials = true; // cookie-сессия (D-07), как credentials:'include'
            headers.forEach((value, name) => xhr.setRequestHeader(name, value));
            xhr.upload.onprogress = (e) => {
                if (!settled)
                    onProgress(e.loaded, e.lengthComputable ? e.total : blob.size);
            };
            xhr.onerror = () => finish(() => reject(networkToError(new TypeError('XHR network error'))));
            xhr.onabort = () => finish(() => reject(new TransportError('INTERNAL', 'request aborted')));
            xhr.onload = () => finish(() => {
                const status = xhr.status;
                const text = xhr.responseText;
                if (status >= 200 && status < 300) {
                    if (text.length === 0)
                        return resolve({});
                    try {
                        resolve(JSON.parse(text));
                    }
                    catch (cause) {
                        reject(new TransportError('INTERNAL', 'malformed JSON response', { status, cause }));
                    }
                    return;
                }
                let errBody;
                try {
                    errBody = text.length > 0 ? JSON.parse(text) : undefined;
                }
                catch {
                    errBody = undefined;
                }
                const error = envelopeToError(status, errBody);
                if (error.code === 'UNAUTHENTICATED' || status === 401)
                    this.onUnauthenticated?.();
                reject(error);
            });
            xhr.send(blob);
        });
    }
    /** Парсит JSON-ответ; пустое тело (204/«») → пустой объект (методы с `{ok:true}` это переживают). */
    async parseJson(res) {
        const text = await res.text();
        if (text.length === 0)
            return {};
        try {
            return JSON.parse(text);
        }
        catch (cause) {
            throw new TransportError('INTERNAL', 'malformed JSON response', {
                status: res.status,
                cause,
            });
        }
    }
    /** Безопасно читает тело ошибки как JSON; не-JSON/обрыв → undefined (даст синтетический INTERNAL). */
    async readBodySafe(res) {
        try {
            const text = await res.text();
            if (text.length === 0)
                return undefined;
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    /** Комбинирует внешний signal и внутренний таймаут в один AbortSignal + cleanup таймера. */
    makeSignal(opts) {
        const ctrl = new AbortController();
        const timeoutMs = opts?.timeoutMs ?? this.defaultTimeoutMs;
        const timer = setTimeout(() => ctrl.abort(new TransportError('INTERNAL', 'request timeout')), timeoutMs);
        const external = opts?.signal;
        let onExternalAbort;
        if (external) {
            if (external.aborted)
                ctrl.abort(external.reason);
            else {
                onExternalAbort = () => ctrl.abort(external.reason);
                external.addEventListener('abort', onExternalAbort, { once: true });
            }
        }
        return {
            signal: ctrl.signal,
            cleanup: () => {
                clearTimeout(timer);
                if (external && onExternalAbort)
                    external.removeEventListener('abort', onExternalAbort);
            },
        };
    }
    /** Добавляет query-параметры к пути (пропуская undefined/null). */
    withQuery(path, query) {
        if (!query)
            return path;
        const usp = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v === undefined || v === null)
                continue;
            usp.set(k, String(v));
        }
        const qs = usp.toString();
        return qs.length > 0 ? `${path}?${qs}` : path;
    }
}
/** Распознаёт отмену (AbortError DOMException или наш TransportError-таймаут). */
/**
 * Тело успешного ответа → Blob (DLP-1). С `onProgress` и потоковым телом — чтение ридером с отчётом
 * после каждого куска; без них — `res.blob()` (и один отчёт в конце, если колбэк есть). Отмена
 * `signal` прерывает чтение: ридер отменяется, частичные байты НЕ возвращаются.
 */
async function readBlob(res, signal, onProgress) {
    const type = res.headers.get('content-type') ?? '';
    const lengthHeader = res.headers.get('content-length');
    const total = lengthHeader !== null && /^\d+$/.test(lengthHeader.trim()) ? Number(lengthHeader) : undefined;
    const body = res.body;
    if (onProgress === undefined || body === null || typeof body.getReader !== 'function') {
        const blob = await res.blob();
        onProgress?.(blob.size, total ?? blob.size);
        return blob;
    }
    const reader = body.getReader();
    const chunks = [];
    let loaded = 0;
    const abortReason = () => signal.reason ?? new TransportError('INTERNAL', 'request aborted');
    // fetch сам рвёт поток по signal, но не каждая реализация (фейки, старые движки) — отменяем явно.
    const onAbort = () => {
        void reader.cancel(signal.reason).catch(() => undefined);
    };
    signal.addEventListener('abort', onAbort, { once: true });
    try {
        onProgress(0, total);
        for (;;) {
            if (signal.aborted)
                throw abortReason();
            const { done, value } = await reader.read();
            if (done)
                break;
            chunks.push(value);
            loaded += value.byteLength;
            onProgress(loaded, total);
        }
        // cancel() завершает ожидающий read() как done — это отмена, а не конец файла.
        if (signal.aborted)
            throw abortReason();
    }
    finally {
        signal.removeEventListener('abort', onAbort);
    }
    return new Blob(chunks, type.length > 0 ? { type } : undefined);
}
function isAbort(cause) {
    if (cause instanceof TransportError)
        return cause.message.includes('timeout') || cause.message.includes('abort');
    return (typeof cause === 'object' &&
        cause !== null &&
        (cause.name === 'AbortError' ||
            cause.code === 20));
}
//# sourceMappingURL=http.js.map